import React, { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { Newspaper } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch, selectBadges } from '@/store';
import { updateBadge } from '@/store/updates-hub/updatesHubSlice';
import { newsService } from '@/services/news.service';
import { PublicNewsItem } from '@/types/news';

export const NewsBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [items, setItems] = useState<PublicNewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const badges = useAppSelector(selectBadges);
  const dispatch = useAppDispatch();

  const sidebarMode = localStorage.getItem('sidebar-mode');
  const unreadCount = sidebarMode === 'expert' ? badges.newsExpert : badges.news;

  useEffect(() => {
    if (!anchorEl) return;
    setLoading(true);
    newsService
      .getNews(1, 5, true)
      .then((res) => setItems(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [anchorEl]);

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await newsService.markAllAsRead();
      dispatch(updateBadge({ key: sidebarMode === 'expert' ? 'newsExpert' : 'news', value: 0 }));
      setItems([]);
    } catch (e) {
      console.error(e);
    } finally {
      setMarkingAll(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'news-popover' : undefined;

  return (
    <>
      <IconButton
        color="inherit"
        aria-describedby={id}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
          <Newspaper />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { mt: 1, width: 360, maxHeight: 400, overflow: 'auto', borderRadius: 2, boxShadow: 3 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Новини</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllAsRead} disabled={markingAll}>
                Позначити всі як прочитані
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Немає непрочитаних новин.
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {items.map((item) => (
                <ListItem
                  key={item.id}
                  component={Link}
                  to={`/news/${item.id}`}
                  onClick={() => setAnchorEl(null)}
                  sx={{
                    alignItems: 'flex-start',
                    py: 1,
                    bgcolor: '#e3f2fd',
                    borderRadius: 1,
                    mb: 0.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { bgcolor: '#bbdefb' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="bold">
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.publishedAt).toLocaleString()}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              component={Link}
              to="/news"
              fullWidth
              sx={{ p: 1, textTransform: 'none' }}
              onClick={() => setAnchorEl(null)}
            >
              Переглянути всі новини
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
